import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useEntitlements } from "../billing/useEntitlements";

const FREE_LIST = [
  "Create a vision, goals, and basic commitments",
  "Check in, complete habits, and use Today",
  "Have a TrustBud and send basic messages",
  "Run focused Accountability",
  "Invite people and manage connections",
  "See current progress and weekly summary",
];

const PLUS_LIST = [
  "Insights: what needs attention, what is working, and today’s move",
  "Plan support: easier habit suggestions and schedule adjustments",
  "Recovery: missed-day reset plans and smallest next steps",
  "Locked-In Support: TrustBud keeps checking in until this habit moves",
  "More Accountability agreements, groups, proof, leaderboards, and history",
  "Smart check-ins: planned-time, recovery, and Accountability support",
  "Relationship Insights: who helps you and who needs attention",
  "Outcome Tracking: see whether changes actually helped",
];

const CONTEXTUAL_COPY = {
  intelligence: {
    title: "Unlock TrustBud Insights",
    subtitle: "See what needs attention, the next useful change, and whether it actually helped.",
    bullets: [
      "Find where consistency is starting to break down",
      "Add recovery moves and schedule adjustments",
      "Track what changed after trying a strategy",
    ],
  },
  today: {
    title: "Unlock Today Insights",
    subtitle: "Get today’s move, a recovery step, and smart support when today starts to slip.",
    bullets: [
      "Know the one move that matters right now",
      "Keep habits alive before a miss becomes drift",
      "Get check-ins when accountability would help",
    ],
  },
  plan: {
    title: "Unlock Plan Insights",
    subtitle: "Make your plan easier to keep with simpler habits, better timing, and recovery-friendly suggestions.",
    bullets: [
      "Protect busy days with clearer timing and lighter plan choices",
      "Spot habits that are too vague or too heavy",
      "Turn patterns into practical plan adjustments",
    ],
  },
  sprint: {
    title: "Unlock Accountability Insights",
    subtitle: "Keep Daily Reports moving with reset support and recaps.",
    bullets: [
      "See when Accountability is starting to fade",
      "Restart with a smaller shared move",
      "Review what worked after the agreement ends",
    ],
  },
  network: {
    title: "Unlock Relationship Insights",
    subtitle: "See who needs attention, who helps you restart, and how to reach out at the right moment.",
    bullets: [
      "Find the TrustBuds who help you recover faster",
      "Get suggested reach-outs and encouragement drafts",
      "Use your network without exposing private details",
    ],
  },
  messages: {
    title: "Unlock accountability drafts",
    subtitle: "Get help saying the right thing when you need support, encouragement, or a restart.",
    bullets: [
      "Draft ask-for-support messages",
      "Send recovery scripts without overthinking",
      "Match the tone to the moment",
    ],
  },
  "weekly-summary": {
    title: "Unlock weekly Insights",
    subtitle: "See what changed, which fixes helped, and where to start next week.",
    bullets: [
      "Review recovery wins and fragile habits",
      "See whether plan changes helped",
      "Choose next week’s highest-leverage focus",
    ],
  },
};

const PRIVACY_POLICY_URL = "https://www.trustbud.app/privacy";
const TERMS_URL = "https://www.apple.com/legal/internet-services/itunes/dev/stdeula/";

export default function TrustBudPlusPage() {
  const [showIosNote, setShowIosNote] = useState(false);
  const { loading, isPlus: isActive } = useEntitlements();
  const [searchParams] = useSearchParams();
  const feature = searchParams.get("feature") || "";
  const contextCopy = CONTEXTUAL_COPY[feature] || null;

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 1.5, md: 0 }, py: { xs: 2, md: 3 }, pb: 5 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          borderRadius: "8px",
          border: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,58,138,0.92) 100%)",
          color: "#ffffff",
          boxShadow: "0 18px 45px rgba(15, 23, 42, 0.12)",
        }}
      >
      <Stack spacing={2}>
        <Typography
          variant="overline"
          sx={{ color: "rgba(255,255,255,0.72)", fontWeight: 900, letterSpacing: 0 }}
        >
          TrustBud Plus
        </Typography>
        <Typography
          variant="h3"
          sx={{ color: "#ffffff", fontWeight: 900, letterSpacing: 0, lineHeight: 1.02, maxWidth: 780 }}
        >
          {contextCopy?.title || "Recover faster with smarter accountability."}
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.78)", maxWidth: 760, fontSize: "1.05rem", lineHeight: 1.65 }}>
          {contextCopy?.subtitle ||
            "TrustBud works for free. Plus adds deeper Insights for people who want help simplifying the plan, recovering faster, and using accountability better."}
        </Typography>
      </Stack>
      </Paper>

      {contextCopy ? (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "primary.main",
            borderRadius: "8px",
            p: 3,
            mb: 3,
            backgroundColor: "#f7faff",
            boxShadow: "0 12px 30px rgba(15, 23, 42, 0.04)",
          }}
        >
          <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 1 }}>
            Why this is Plus
          </Typography>
          <Stack component="ul" spacing={0.8} sx={{ pl: 2, m: 0 }}>
            {contextCopy.bullets.map((item) => (
              <Typography component="li" variant="body2" color="text.secondary" key={item}>
                {item}
              </Typography>
            ))}
          </Stack>
        </Paper>
      ) : null}

      {!loading && isActive && (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px",
            p: 2.25,
            mb: 3,
            backgroundColor: "rgba(30,58,138,0.04)",
          }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Active subscription
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You&apos;re currently on TrustBud Plus.
          </Typography>
        </Paper>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              borderRadius: "8px",
              border: "1px solid",
              borderColor: "divider",
              height: "100%",
              boxShadow: "0 12px 30px rgba(15, 23, 42, 0.04)",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight={800}>
                  What stays free
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  The core product stays intact. Free users still get the most important part: help when they are starting to slip.
                </Typography>
                <Stack component="ul" spacing={0.9} sx={{ pl: 2, m: 0 }}>
                  {FREE_LIST.map((item) => (
                    <Typography component="li" variant="body2" color="text.secondary" key={item}>
                      {item}
                    </Typography>
                  ))}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              borderRadius: "8px",
              border: "1px solid",
              borderColor: "primary.main",
              backgroundColor: "#f7faff",
              height: "100%",
              boxShadow: "0 12px 30px rgba(15, 23, 42, 0.05)",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight={800}>
                  What Plus unlocks
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Plus connects patterns to small fixes and tracks whether those fixes helped.
                </Typography>
                <Stack component="ul" spacing={0.9} sx={{ pl: 2, m: 0 }}>
                  {PLUS_LIST.map((item) => (
                    <Typography component="li" variant="body2" color="text.secondary" key={item}>
                      {item}
                    </Typography>
                  ))}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={7}>
          <Card
            elevation={0}
            sx={{
              borderRadius: "8px",
              border: "1px solid",
              borderColor: "divider",
              height: "100%",
              boxShadow: "0 12px 30px rgba(15, 23, 42, 0.04)",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={1.5}>
                <Typography variant="h6" fontWeight={800}>
                  Why someone upgrades
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Upgrade when you want TrustBud to help when the plan gets hard.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Plus detects where consistency starts breaking, recommends a smaller fix, helps apply it, then shows whether anything changed.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  The point is still the same: catch drift early, restart quickly, and stay visible enough that follow-through does not quietly die off.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card
            elevation={0}
            sx={{
              borderRadius: "8px",
              border: "1px solid",
              borderColor: "divider",
              background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
              color: "#ffffff",
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.72)" }}>
                  Subscription details
                </Typography>
                <Typography variant="h5" fontWeight={900}>
                  $4.99 / month
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.78)" }}>
                  Auto-renewing monthly subscription. Cancel anytime in App Store settings.
                </Typography>
                <Divider sx={{ borderColor: "rgba(255,255,255,0.16)" }} />
                <Stack direction="row" spacing={2}>
                  <Typography
                    component="a"
                    href={PRIVACY_POLICY_URL}
                    target="_blank"
                    rel="noreferrer"
                    variant="caption"
                    sx={{ color: "#ffffff", fontWeight: 700 }}
                  >
                    Privacy Policy
                  </Typography>
                  <Typography
                    component="a"
                    href={TERMS_URL}
                    target="_blank"
                    rel="noreferrer"
                    variant="caption"
                    sx={{ color: "#ffffff", fontWeight: 700 }}
                  >
                    Terms of Use
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "8px",
          p: 2.5,
          mb: 3,
          backgroundColor: "#ffffff",
          boxShadow: "0 12px 30px rgba(15, 23, 42, 0.04)",
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          Philosophy
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You do not lose your work if you stay on the free plan. Nothing essential is held hostage. Plus adds recovery, plan Insights, smarter nudges, relationship Insights, and outcome tracking on top of the core accountability loop.
        </Typography>
      </Paper>

      <Stack spacing={1.25}>
        {isActive ? (
          <Button variant="contained" onClick={() => setShowIosNote(true)}>
            Manage subscription
          </Button>
        ) : (
          <>
            <Button variant="contained" onClick={() => setShowIosNote(true)}>
              Upgrade to TrustBud Plus
            </Button>
            <Button variant="text" onClick={() => setShowIosNote(true)}>
              Restore purchase
            </Button>
          </>
        )}
        {showIosNote && (
          <Typography variant="caption" color="text.secondary">
            Subscriptions are currently available in the iOS app via the App Store.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
